import React, { useState, useEffect } from 'react';
import './HolidayCalendarPage.css';
import { API_BASE_URL, api } from '../services/api';

const initialState = {
  holiday_date: '',
  reason: '',
  fund_centre_id: ''
};

const HolidayCalendarPage = () => {
  const [tab, setTab] = useState('add');
  const [holidays, setHolidays] = useState([]);
  const [fundCentres, setFundCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialState);
  const [search, setSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    fetchHolidays();
    fetchFundCentres();
  }, []);

  function initialForm() {
    return { ...initialState };
  }

  const fetchHolidays = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${API_BASE_URL}/holiday-calendar`);
      if (response.data.success) {
        setHolidays(response.data.data || []);
      } else {
        setError('Failed to fetch holidays');
      }
    } catch (err) {
      console.error('Error fetching holidays:', err);
      setError(err.response?.data?.error || 'Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  };

  const fetchFundCentres = async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/fund-centre-master/dropdown/list`);
      if (response.data.success) {
        setFundCentres(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching fund centres:', err);
      // Don't show error for fund centres, just log it
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!form.holiday_date || !form.reason.trim()) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      if (editing) {
        await api.put(`${API_BASE_URL}/holiday-calendar/${editing.id}`, form);
        setSuccess('Holiday updated successfully');
        setEditing(null);
      } else {
        await api.post(`${API_BASE_URL}/holiday-calendar`, form);
        setSuccess('Holiday added successfully');
      }
      setForm(initialForm());
      fetchHolidays();
    } catch (err) {
      console.error('Error saving holiday:', err);
      setError(err.response?.data?.error || 'Failed to save holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (holiday) => {
    setForm({
      holiday_date: holiday.holiday_date,
      reason: holiday.reason,
      fund_centre_id: holiday.fund_centre_id || ''
    });
    setEditing(holiday);
    setTab('add');
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`${API_BASE_URL}/holiday-calendar/${id}`);
      setSuccess('Holiday deleted successfully');
      fetchHolidays();
    } catch (err) {
      console.error('Error deleting holiday:', err);
      setError(err.response?.data?.error || 'Failed to delete holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm(initialForm());
    setEditing(null);
    setError('');
    setSuccess('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get all years from holidays
  const years = [...new Set(holidays.map(h => new Date(h.holiday_date).getFullYear()))].sort((a, b) => b - a);
  if (years.length === 0) {
    years.push(new Date().getFullYear());
  }

  // Get fund centre name by ID
  const getFundCentreName = (fundCentreId) => {
    if (!fundCentreId) return 'N/A';
    const fundCentre = fundCentres.find(fc => fc.id === fundCentreId);
    return fundCentre ? `${fundCentre.name} (${fundCentre.fund_centre_code})` : 'N/A';
  };

  // Filter holidays based on search, year, and month
  const filteredHolidays = holidays.filter(holiday => {
    const holidayDate = new Date(holiday.holiday_date);
    const matchesYear = holidayDate.getFullYear() === selectedYear;
    const matchesMonth = (holidayDate.getMonth() + 1) === selectedMonth;
    const matchesSearch = search === '' || 
      holiday.reason?.toLowerCase().includes(search.toLowerCase()) ||
      formatDate(holiday.holiday_date).toLowerCase().includes(search.toLowerCase()) ||
      getFundCentreName(holiday.fund_centre_id).toLowerCase().includes(search.toLowerCase());
    
    return matchesYear && matchesMonth && matchesSearch;
  });

  return (
    <div className="holiday-calendar-page ui-page">
      {success && (
        <div className="success-message">{success}</div>
      )}
      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="tabs">
        <button
          className={tab === 'add' ? 'active' : ''}
          onClick={() => {
            setTab('add');
            setEditing(null);
            setForm(initialForm());
            setError('');
            setSuccess('');
          }}
        >
          Add Holiday
        </button>
        <button
          className={tab === 'search' ? 'active' : ''}
          onClick={() => {
            setTab('search');
            setError('');
            setSuccess('');
          }}
        >
          Search Holiday
        </button>
      </div>

      {tab === 'add' && (
        <form className="master-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Holiday Date <span className="required">*</span></label>
              <input
                type="date"
                name="holiday_date"
                value={form.holiday_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Fund Centre</label>
              <select
                name="fund_centre_id"
                value={form.fund_centre_id}
                onChange={handleChange}
              >
                <option value="">Select Fund Centre (Optional)</option>
                {fundCentres.map(fc => (
                  <option key={fc.id} value={fc.id}>
                    {fc.name} ({fc.fund_centre_code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Reason <span className="required">*</span></label>
              <input
                type="text"
                name="reason"
                value={form.reason}
                onChange={handleChange}
                placeholder="Enter reason for holiday"
                required
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-btn">
              {editing ? 'Update' : 'Add'} Holiday
            </button>
            {editing && (
              <button
                type="button"
                onClick={handleCancel}
                className="cancel-btn"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      )}

      {tab === 'search' && (
        <div className="search-section">
            <input
            type="text"
            placeholder="Search by reason, date, or fund centre..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="search-input"
          />

          <div className="filter-section">
            <label>Filter by:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                <option key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleDateString('en-US', { month: 'long' })}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setSelectedYear(new Date().getFullYear());
                setSelectedMonth(new Date().getMonth() + 1);
                setSearch('');
              }}
            >
              Reset to Current Month
            </button>
          </div>

          {loading && <div className="loading">Loading...</div>}
          <table className="master-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Fund Centre</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredHolidays.length === 0 && !loading && (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                    No holidays found.
                  </td>
                </tr>
              )}
              {filteredHolidays.map((holiday) => (
                <tr key={holiday.id}>
                  <td>{formatDate(holiday.holiday_date)}</td>
                  <td>{getFundCentreName(holiday.fund_centre_id)}</td>
                  <td>{holiday.reason}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(holiday)}
                      className="edit-btn"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(holiday.id)}
                      className="delete-btn"
                      title="Delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HolidayCalendarPage;
